/**
 * GEDCOM Export Utility
 * Converts family tree data back to GEDCOM format for saving
 */

/**
 * Convert family tree data back to GEDCOM format
 * @param {Object} gedcomData - Parsed family tree data
 * @returns {string} GEDCOM formatted string
 */
export const exportToGedcom = (gedcomData) => {
  const { individuals, families } = gedcomData;
  let gedcomContent = [];
  
  // GEDCOM Header
  gedcomContent.push('0 HEAD');
  gedcomContent.push('1 SOUR MarcoDeMarco.net');
  gedcomContent.push('2 NAME MarcoDeMarco.net Family Tree');
  gedcomContent.push('2 VERS 1.0');
  gedcomContent.push('1 DEST DISKETTE');
  gedcomContent.push(`1 DATE ${new Date().toLocaleDateString('en-GB', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric' 
  }).toUpperCase()}`);
  gedcomContent.push(`2 TIME ${new Date().toLocaleTimeString('en-GB', { hour12: false })}`);
  gedcomContent.push('1 GEDC');
  gedcomContent.push('2 VERS 5.5.1');
  gedcomContent.push('2 FORM LINEAGE-LINKED');
  gedcomContent.push('1 CHAR UTF-8');
  gedcomContent.push('1 FILE rossetti_updated.ged');
  
  // Export Individuals
  Object.entries(individuals).forEach(([id, person]) => {
    gedcomContent.push(`0 ${id} INDI`);
    
    // Name
    if (person.name) {
      // Convert name to GEDCOM format (surname in slashes)
      const nameParts = person.name.trim().split(' ');
      const surname = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
      const givenNames = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : person.name;
      const gedcomName = surname ? `${givenNames} /${surname}/` : person.name;
      
      gedcomContent.push(`1 NAME ${gedcomName}`);
      gedcomContent.push('2 TYPE BIRTH');
      if (givenNames) gedcomContent.push(`2 GIVN ${givenNames}`);
      if (surname) gedcomContent.push(`2 SURN ${surname}`);
    }
    
    // Sex
    if (person.sex) {
      gedcomContent.push(`1 SEX ${person.sex}`);
    }
    
    // Birth
    if (person.birth || person.birthPlace) {
      gedcomContent.push('1 BIRT');
      if (person.birth) {
        gedcomContent.push(`2 DATE ${person.birth}`);
      }
      if (person.birthPlace) {
        gedcomContent.push(`2 PLAC ${person.birthPlace}`);
      }
    }
    
    // Death
    if (person.death || person.deathPlace) {
      gedcomContent.push('1 DEAT');
      if (person.death) {
        gedcomContent.push(`2 DATE ${person.death}`);
      }
      if (person.deathPlace) {
        gedcomContent.push(`2 PLAC ${person.deathPlace}`);
      }
    }
    
    // Notes
    if (person.note) {
      const noteLines = person.note.split('\n');
      gedcomContent.push(`1 NOTE ${noteLines[0] || ''}`);
      noteLines.slice(1).forEach(line => {
        if (line.trim()) {
          gedcomContent.push(`2 CONT ${line}`);
        }
      });
    }
    
    // Family relationships
    if (person.parents) {
      gedcomContent.push(`1 FAMC ${person.parents}`);
    }
    
    person.families.forEach(familyId => {
      gedcomContent.push(`1 FAMS ${familyId}`);
    });
    
    // Change date
    gedcomContent.push('1 CHAN');
    gedcomContent.push(`2 DATE ${new Date().toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    }).toUpperCase()}`);
    gedcomContent.push(`3 TIME ${new Date().toLocaleTimeString('en-GB', { hour12: false })}`);
    gedcomContent.push('2 _WT_USER MarcoDeMarco.net');
  });
  
  // Export Families
  Object.entries(families).forEach(([id, family]) => {
    gedcomContent.push(`0 ${id} FAM`);
    
    // Husband
    if (family.husband) {
      gedcomContent.push(`1 HUSB ${family.husband}`);
    }
    
    // Wife
    if (family.wife) {
      gedcomContent.push(`1 WIFE ${family.wife}`);
    }
    
    // Children
    if (family.children && family.children.length > 0) {
      family.children.forEach(childId => {
        gedcomContent.push(`1 CHIL ${childId}`);
      });
    }
    
    // Marriage
    if (family.marriage) {
      gedcomContent.push('1 MARR');
      gedcomContent.push(`2 DATE ${family.marriage}`);
    }
    
    // Change date
    gedcomContent.push('1 CHAN');
    gedcomContent.push(`2 DATE ${new Date().toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    }).toUpperCase()}`);
    gedcomContent.push(`3 TIME ${new Date().toLocaleTimeString('en-GB', { hour12: false })}`);
    gedcomContent.push('2 _WT_USER MarcoDeMarco.net');
  });
  
  // GEDCOM Trailer
  gedcomContent.push('0 TRLR');
  
  return gedcomContent.join('\n');
};

/**
 * Download GEDCOM file to user's computer
 * @param {Object} gedcomData - Parsed family tree data
 * @param {string} filename - Optional filename (default: rossetti_updated.ged)
 */
export const downloadGedcomFile = (gedcomData, filename = 'rossetti_updated.ged') => {
  try {
    const gedcomContent = exportToGedcom(gedcomData);
    
    // Create blob and download
    const blob = new Blob([gedcomContent], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    
    // Create temporary download link
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error downloading GEDCOM file:', error);
    return false;
  }
};

/**
 * Save GEDCOM data to localStorage for persistence
 * @param {Object} gedcomData - Parsed family tree data
 * @param {string} key - Storage key (default: 'rossetti_gedcom_data')
 */
export const saveToLocalStorage = (gedcomData, key = 'rossetti_gedcom_data') => {
  try {
    const gedcomContent = exportToGedcom(gedcomData);
    const dataToStore = {
      gedcomContent,
      lastModified: new Date().toISOString(),
      version: '1.0'
    };
    
    localStorage.setItem(key, JSON.stringify(dataToStore));
    localStorage.setItem(`${key}_backup`, JSON.stringify(dataToStore));
    
    return true;
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    return false;
  }
};

/**
 * Load GEDCOM data from localStorage
 * @param {string} key - Storage key (default: 'rossetti_gedcom_data')
 * @returns {Object|null} Stored GEDCOM data or null if not found
 */
export const loadFromLocalStorage = (key = 'rossetti_gedcom_data') => {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
    return null;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return null;
  }
};

/**
 * Check if there are unsaved changes in localStorage
 * @param {string} key - Storage key (default: 'rossetti_gedcom_data')
 * @returns {boolean} True if there are stored changes
 */
export const hasStoredChanges = (key = 'rossetti_gedcom_data') => {
  return localStorage.getItem(key) !== null;
};

/**
 * Clear stored GEDCOM data from localStorage
 * @param {string} key - Storage key (default: 'rossetti_gedcom_data')
 */
export const clearStoredData = (key = 'rossetti_gedcom_data') => {
  localStorage.removeItem(key);
  localStorage.removeItem(`${key}_backup`);
};

/**
 * Get change statistics
 * @param {Object} gedcomData - Current GEDCOM data
 * @returns {Object} Statistics about the data
 */
export const getChangeStats = (gedcomData) => {
  const { individuals, families } = gedcomData;
  
  return {
    totalIndividuals: Object.keys(individuals).length,
    totalFamilies: Object.keys(families).length,
    lastModified: new Date().toISOString(),
    exportSize: exportToGedcom(gedcomData).length
  };
};